import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Plus, Building2, Clock, CheckCircle, XCircle, LogOut, Edit, Trash2, Wallet as WalletIcon, Heart, MapPin, Star, Sparkles, BarChart3, Navigation, Globe, Shield, Copy, ExternalLink, History, Eye, Settings, Key, EyeIcon, EyeOff, ShoppingBag, Car } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Wallet from '@/components/Wallet';
import FavoriteButton from '@/components/FavoriteButton';
import BoostBadgeDialog from '@/components/BoostBadgeDialog';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import LocationsManager from '@/components/LocationsManager';
import DomainVerification from '@/components/DomainVerification';
import SocialProofVerification from '@/components/SocialProofVerification';
import BusinessProducts from '@/components/BusinessProducts';
import DeliveryDriverDashboard from '@/components/DeliveryDriverDashboard';
import { useFeatureFlags, useUserDeliveryAccess } from '@/hooks/useFeatureFlags';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { resolveImageUrl } from '@/utils/imageUrl';
import { OptimizedImage } from '@/components/OptimizedImage';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

export default function UserDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'businesses');
  const [user, setUser] = useState(location.state?.user || null);
  const [businesses, setBusinesses] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [boostDialogOpen, setBoostDialogOpen] = useState(false);
  const [businessToBoost, setBusinessToBoost] = useState(null);
  const [selectedAnalyticsBusiness, setSelectedAnalyticsBusiness] = useState(null);
  const [selectedLocationsBusiness, setSelectedLocationsBusiness] = useState(null);
  const [createOnlineDialogOpen, setCreateOnlineDialogOpen] = useState(false);
  const [businessForOnline, setBusinessForOnline] = useState(null);
  const [onlineFormData, setOnlineFormData] = useState({ service_area: 'Nationwide', description: '' });
  const [creatingOnline, setCreatingOnline] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  // Password change state
  const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  // Marketplace & Delivery state
  const [selectedProductsBusiness, setSelectedProductsBusiness] = useState(null);
  
  // Feature flags hooks
  const { marketplaceEnabled, deliveryEnabled } = useFeatureFlags();
  const deliveryAccess = useUserDeliveryAccess();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user if not passed via state
        if (!user) {
          const userRes = await fetch(`${API}/auth/me`, {
            credentials: 'include'
          });
          
          if (!userRes.ok) {
            navigate('/auth', { replace: true });
            return;
          }
          
          const userData = await userRes.json();
          setUser(userData);
        }

        // Fetch user's businesses
        const bizRes = await fetch(`${API}/businesses/user/list`, {
          credentials: 'include'
        });
        
        if (bizRes.ok) {
          const bizData = await bizRes.json();
          setBusinesses(bizData.businesses || bizData || []);
        }

        // Fetch wallet balance
        const walletRes = await fetch(`${API}/wallet`, {
          credentials: 'include'
        });
        
        if (walletRes.ok) {
          const walletData = await walletRes.json();
          setWalletBalance(walletData.balance + walletData.pending_balance);
        }

        // Fetch favorites
        const favRes = await fetch(`${API}/favorites`, {
          credentials: 'include'
        });
        
        if (favRes.ok) {
          const favData = await favRes.json();
          setFavorites(favData.favorites || favData || []);
        }

        // Fetch recently viewed
        const recentRes = await fetch(`${API}/recently-viewed`, {
          credentials: 'include'
        });
        
        if (recentRes.ok) {
          const recentData = await recentRes.json();
          setRecentlyViewed(recentData.businesses || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, user]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const refreshWalletBalance = async () => {
    try {
      const walletRes = await fetch(`${API}/wallet`, {
        credentials: 'include'
      });
      
      if (walletRes.ok) {
        const walletData = await walletRes.json();
        setWalletBalance(walletData.balance + walletData.pending_balance);
      }
    } catch (error) {
      console.error('Error refreshing wallet:', error);
    }
  };

  const handleLogout = async () => {
    try {
      const { clearSessionToken, authFetch } = await import('../utils/authToken');
      await authFetch(`${API}/auth/logout`, {
        method: 'POST'
      });
      clearSessionToken();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('All fields are required');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      if (response.ok) {
        toast.success('Password changed successfully');
        setPasswordChangeOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDelete = async () => {
    if (!businessToDelete) return;

    try {
      const response = await fetch(`${API}/businesses/${businessToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setBusinesses(businesses.filter(b => b.business_id !== businessToDelete));
        toast.success('Business deleted successfully');
      } else {
        toast.error('Failed to delete business');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete business');
    } finally {
      setDeleteDialogOpen(false);
      setBusinessToDelete(null);
    }
  };

  const openCreateOnlineDialog = (business) => {
    setBusinessForOnline(business);
    setOnlineFormData({
      service_area: 'Nationwide',
      description: `${business.description || ''} - Now available online! Shop from anywhere.`
    });
    setCreateOnlineDialogOpen(true);
  };

  const handleCreateOnlineVersion = async () => {
    if (!businessForOnline) return;
    
    setCreatingOnline(true);
    try {
      const response = await fetch(`${API}/businesses/${businessForOnline.business_id}/create-online-version`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(onlineFormData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Online version created! It will appear after verification.');
        // Refresh businesses list
        const bizRes = await fetch(`${API}/businesses/user/list`, { credentials: 'include' });
        if (bizRes.ok) {
          const bizData = await bizRes.json();
          setBusinesses(bizData.businesses || bizData || []);
        }
        setCreateOnlineDialogOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create online version');
      }
    } catch (error) {
      console.error('Create online version error:', error);
      toast.error('Failed to create online version');
    } finally {
      setCreatingOnline(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="verified-badge">
            <CheckCircle className="w-3 h-3" />
            Verified
          </span>
        );
      case 'pending':
        return (
          <span className="pending-badge">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="rejected-badge">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-24 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="h-8 w-48 skeleton rounded mb-8" />
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 skeleton rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                Dashboard
              </p>
              <h1 className="text-3xl font-bold text-white tracking-tight">
                Welcome, {user?.name?.split(' ')[0] || 'User'}
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              <Link
                to="/submit-business"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="add-business-button"
              >
                <Plus className="w-4 h-4" />
                Add Business
              </Link>
              {user?.auth_type !== 'google' && (
                <button
                  onClick={() => setPasswordChangeOpen(true)}
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="change-password-button"
                >
                  <Key className="w-4 h-4" />
                  Change Password
                </button>
              )}
              <button
                onClick={handleLogout}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>

          {/* User Info Card with Wallet Preview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-6 mb-8"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center text-2xl font-bold text-white">
                    {user?.name?.[0] || 'U'}
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-semibold text-white">{user?.name}</h2>
                  <p className="text-zinc-400">{user?.email}</p>
                </div>
              </div>

              {/* Wallet Preview */}
              <button
                onClick={() => handleTabChange('wallet')}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors group"
                data-testid="wallet-preview-button"
              >
                <img 
                  src={MELANIN_ICON} 
                  alt="Melanin" 
                  className="w-8 h-8 rounded-full"
                />
                <div className="text-left">
                  <p className="text-zinc-500 text-xs">Melanin Balance</p>
                  <p className="text-xl font-bold text-white group-hover:text-green-400 transition-colors">
                    {walletBalance.toFixed(2)} MEL
                  </p>
                </div>
              </button>
            </div>
          </motion.div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-zinc-800 pb-4">
            <button
              onClick={() => handleTabChange('businesses')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'businesses'
                  ? 'bg-white text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
              data-testid="tab-businesses"
            >
              <Building2 className="w-4 h-4" />
              My Businesses
            </button>
            <button
              onClick={() => handleTabChange('favorites')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'favorites'
                  ? 'bg-white text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
              data-testid="tab-favorites"
            >
              <Heart className="w-4 h-4" />
              Favorites
              {favorites.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">
                  {favorites.length}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('wallet')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'wallet'
                  ? 'bg-white text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
              data-testid="tab-wallet"
            >
              <WalletIcon className="w-4 h-4" />
              Wallet
            </button>
            <button
              onClick={() => handleTabChange('locations')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'locations'
                  ? 'bg-white text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
              data-testid="tab-locations"
            >
              <Navigation className="w-4 h-4" />
              Locations
            </button>
            <button
              onClick={() => handleTabChange('analytics')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'analytics'
                  ? 'bg-white text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
              data-testid="tab-analytics"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </button>
            <button
              onClick={() => handleTabChange('history')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === 'history'
                  ? 'bg-white text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
              data-testid="tab-history"
            >
              <History className="w-4 h-4" />
              History
              {recentlyViewed.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400">
                  {recentlyViewed.length}
                </span>
              )}
            </button>
            {/* Products Tab - Only show if marketplace is enabled and user has claimed businesses */}
            {marketplaceEnabled && businesses.some(b => b.is_claimed && b.verification_status === 'verified' && b.marketplace_enabled !== false) && (
              <button
                onClick={() => handleTabChange('products')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === 'products'
                    ? 'bg-white text-black'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white'
                }`}
                data-testid="tab-products"
              >
                <ShoppingBag className="w-4 h-4" />
                My Products
              </button>
            )}
            {/* Delivery Tab - Only show if delivery is enabled */}
            {deliveryEnabled && (
              <button
                onClick={() => handleTabChange('delivery')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === 'delivery'
                    ? 'bg-white text-black'
                    : 'bg-zinc-900 text-zinc-400 hover:text-white'
                }`}
                data-testid="tab-delivery"
              >
                <Car className="w-4 h-4" />
                Delivery
              </button>
            )}
          </div>

          {/* Tab Content */}
          {activeTab === 'businesses' ? (
            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Your Businesses
              </h2>

              {businesses.length > 0 ? (
                <div className="space-y-4">
                  {businesses.map((business, index) => (
                    <motion.div
                      key={business.business_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="glass-card rounded-xl p-6"
                      data-testid={`business-item-${business.business_id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                            <Building2 className="w-6 h-6 text-zinc-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="text-lg font-semibold text-white">
                                {business.name}
                              </h3>
                              {getStatusBadge(business.verification_status)}
                            </div>
                            <p className="text-zinc-500 text-sm">{business.category}</p>
                            {business.verification_status === 'rejected' && business.rejection_reason && (
                              <p className="text-red-400 text-sm mt-2">
                                Reason: {business.rejection_reason}
                              </p>
                            )}
                            {business.verification_status === 'pending' && (
                              <p className="text-yellow-400/70 text-xs mt-2 flex items-center gap-1">
                                <img src={MELANIN_ICON} alt="" className="w-3 h-3" />
                                +0.50 MEL pending verification
                              </p>
                            )}
                            
                            {/* Online Business Verification Options */}
                            {business.is_online_only && (
                              <div className="flex flex-wrap items-center gap-2 mt-3">
                                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/10 text-purple-400 text-xs">
                                  <Globe className="w-3 h-3" />
                                  Online Business
                                </span>
                                
                                {/* Social Proof Verification */}
                                <SocialProofVerification
                                  businessId={business.business_id}
                                  existingData={business.social_proof_data}
                                  onVerified={(score) => {
                                    // Update local state
                                    setBusinesses(prev => prev.map(b => 
                                      b.business_id === business.business_id 
                                        ? { ...b, confidence_score: score }
                                        : b
                                    ));
                                  }}
                                />

                                {/* Domain Verification */}
                                {business.website && (
                                  <DomainVerification
                                    businessId={business.business_id}
                                    website={business.website}
                                    isVerified={business.domain_verified}
                                    onVerified={() => {
                                      setBusinesses(prev => prev.map(b => 
                                        b.business_id === business.business_id 
                                          ? { ...b, domain_verified: true }
                                          : b
                                      ));
                                    }}
                                  />
                                )}

                                {/* Show confidence score if exists */}
                                {business.confidence_score > 0 && (
                                  <span className={`px-2 py-1 rounded-full text-xs ${
                                    business.confidence_score >= 70 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : business.confidence_score >= 40
                                      ? 'bg-yellow-500/20 text-yellow-400'
                                      : 'bg-red-500/20 text-red-400'
                                  }`}>
                                    {business.confidence_score}% Confidence
                                  </span>
                                )}

                                {/* Domain Verified Badge */}
                                {business.domain_verified && (
                                  <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs">
                                    <Shield className="w-3 h-3" />
                                    Domain Verified
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Local Business - Create Online Version or Show Link */}
                            {!business.is_online_only && (
                              <div className="flex flex-wrap items-center gap-2 mt-3">
                                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs">
                                  <MapPin className="w-3 h-3" />
                                  Local Business
                                </span>
                                
                                {business.linked_online_business_id ? (
                                  <Link
                                    to={`/businesses/${business.linked_online_business_id}`}
                                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-purple-500/10 text-purple-400 text-xs hover:bg-purple-500/20 transition-colors"
                                  >
                                    <Globe className="w-3 h-3" />
                                    View Online Listing
                                    <ExternalLink className="w-3 h-3" />
                                  </Link>
                                ) : (
                                  <button
                                    onClick={() => openCreateOnlineDialog(business)}
                                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 text-xs hover:from-purple-500/30 hover:to-pink-500/30 transition-colors border border-purple-500/30"
                                    data-testid={`create-online-${business.business_id}`}
                                  >
                                    <Copy className="w-3 h-3" />
                                    Create Online Version
                                  </button>
                                )}
                              </div>
                            )}
                            
                            {/* Online Business - Show Link to Local */}
                            {business.is_online_only && business.linked_local_business_id && (
                              <div className="mt-2">
                                <Link
                                  to={`/businesses/${business.linked_local_business_id}`}
                                  className="flex items-center gap-1 text-blue-400 text-xs hover:underline"
                                >
                                  <MapPin className="w-3 h-3" />
                                  View physical location(s)
                                  <ExternalLink className="w-3 h-3" />
                                </Link>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {business.verification_status === 'verified' && (
                            <button
                              onClick={() => {
                                setBusinessToBoost(business);
                                setBoostDialogOpen(true);
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-400 hover:from-purple-500/30 hover:to-pink-500/30 transition-all text-sm font-medium"
                              data-testid={`promote-business-${business.business_id}`}
                            >
                              <Sparkles className="w-3.5 h-3.5" />
                              Promote
                            </button>
                          )}
                          <Link
                            to={`/submit-business?edit=${business.business_id}`}
                            className="w-9 h-9 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
                            data-testid={`edit-business-${business.business_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => {
                              setBusinessToDelete(business.business_id);
                              setDeleteDialogOpen(true);
                            }}
                            className="w-9 h-9 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-red-400 hover:border-red-900 transition-colors"
                            data-testid={`delete-business-${business.business_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="glass-card rounded-xl p-12 text-center">
                  <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No businesses yet</h3>
                  <p className="text-zinc-500 mb-6">
                    Start by adding your first Black-owned business to the directory.
                  </p>
                  <Link
                    to="/submit-business"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="add-first-business-button"
                  >
                    <Plus className="w-4 h-4" />
                    Add Your Business
                  </Link>
                </div>
              )}
            </div>
          ) : activeTab === 'favorites' ? (
            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Saved Businesses
              </h2>

              {favorites.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {favorites.map((item, index) => (
                    <motion.div
                      key={item.favorite_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={`/businesses/${item.business_id}`}
                        className="block glass-card rounded-xl overflow-hidden hover:border-zinc-700 transition-colors"
                        data-testid={`favorite-item-${item.business_id}`}
                      >
                        <div className="relative aspect-video">
                          <OptimizedImage
                            src={item.image_url}
                            fallback="https://images.pexels.com/photos/10375895/pexels-photo-10375895.jpeg"
                            alt={item.name}
                            preset="card"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                          
                          {/* Favorite Button */}
                          <div className="absolute top-3 right-3">
                            <FavoriteButton 
                              businessId={item.business_id} 
                              size="sm"
                              onToggle={(isFav) => {
                                if (!isFav) {
                                  setFavorites(favorites.filter(f => f.business_id !== item.business_id));
                                }
                              }}
                            />
                          </div>

                          <div className="absolute bottom-0 left-0 right-0 p-4">
                            <p className="text-zinc-400 text-xs uppercase tracking-wider mb-1">
                              {item.category}
                            </p>
                            <h3 className="text-lg font-semibold text-white mb-1">
                              {item.name}
                            </h3>
                            
                            {/* Rating */}
                            {item.review_count > 0 && (
                              <div className="flex items-center gap-2 mb-2">
                                <div className="flex items-center gap-0.5">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                      key={star}
                                      className={`w-3 h-3 ${
                                        star <= Math.round(item.average_rating)
                                          ? 'text-yellow-400 fill-yellow-400'
                                          : 'text-zinc-600'
                                      }`}
                                    />
                                  ))}
                                </div>
                                <span className="text-zinc-400 text-xs">
                                  ({item.review_count})
                                </span>
                              </div>
                            )}
                            
                            {item.city && (
                              <div className="flex items-center gap-1 text-zinc-400 text-sm">
                                <MapPin className="w-3 h-3" />
                                <span>{item.city}, {item.state}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="glass-card rounded-xl p-12 text-center">
                  <Heart className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No favorites yet</h3>
                  <p className="text-zinc-500 mb-6">
                    Browse the directory and save businesses you want to remember.
                  </p>
                  <Link
                    to="/businesses"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="browse-businesses-button"
                  >
                    Browse Businesses
                  </Link>
                </div>
              )}
            </div>
          ) : activeTab === 'wallet' ? (
            <Wallet onBalanceChange={setWalletBalance} />
          ) : activeTab === 'analytics' ? (
            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Business Analytics
              </h2>

              {businesses.filter(b => b.verification_status === 'verified').length > 0 ? (
                <div className="space-y-6">
                  {/* Business Selector */}
                  <div className="flex flex-wrap gap-2">
                    {businesses
                      .filter(b => b.verification_status === 'verified')
                      .map((biz) => (
                        <button
                          key={biz.business_id}
                          onClick={() => setSelectedAnalyticsBusiness(biz)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            selectedAnalyticsBusiness?.business_id === biz.business_id
                              ? 'bg-white text-black'
                              : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white'
                          }`}
                          data-testid={`analytics-select-${biz.business_id}`}
                        >
                          {biz.name}
                        </button>
                      ))}
                  </div>

                  {/* Analytics Dashboard */}
                  {selectedAnalyticsBusiness ? (
                    <AnalyticsDashboard
                      businessId={selectedAnalyticsBusiness.business_id}
                      businessName={selectedAnalyticsBusiness.name}
                    />
                  ) : (
                    <div className="glass-card rounded-xl p-12 text-center">
                      <BarChart3 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-white mb-2">Select a Business</h3>
                      <p className="text-zinc-500">
                        Choose one of your verified businesses above to view its analytics.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="glass-card rounded-xl p-12 text-center">
                  <BarChart3 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Analytics Available</h3>
                  <p className="text-zinc-500 mb-6">
                    Analytics are available for verified businesses only. Submit a business to get started.
                  </p>
                  <Link
                    to="/submit-business"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="add-business-for-analytics"
                  >
                    <Plus className="w-4 h-4" />
                    Add Your Business
                  </Link>
                </div>
              )}
            </div>
          ) : activeTab === 'locations' ? (
            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Manage Business Locations
              </h2>

              {businesses.filter(b => b.verification_status === 'verified').length > 0 ? (
                <div className="space-y-6">
                  {/* Business Selector */}
                  <div className="flex flex-wrap gap-2">
                    {businesses
                      .filter(b => b.verification_status === 'verified')
                      .map((biz) => (
                        <button
                          key={biz.business_id}
                          onClick={() => setSelectedLocationsBusiness(biz)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            selectedLocationsBusiness?.business_id === biz.business_id
                              ? 'bg-white text-black'
                              : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white'
                          }`}
                          data-testid={`locations-select-${biz.business_id}`}
                        >
                          {biz.name}
                        </button>
                      ))}
                  </div>

                  {/* Locations Manager */}
                  {selectedLocationsBusiness ? (
                    <LocationsManager
                      businessId={selectedLocationsBusiness.business_id}
                      businessName={selectedLocationsBusiness.name}
                    />
                  ) : (
                    <div className="glass-card rounded-xl p-12 text-center">
                      <Navigation className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-white mb-2">Select a Business</h3>
                      <p className="text-zinc-500">
                        Choose one of your verified businesses above to manage its locations.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="glass-card rounded-xl p-12 text-center">
                  <Navigation className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Businesses Available</h3>
                  <p className="text-zinc-500 mb-6">
                    Locations management is available for verified businesses only. Submit a business to get started.
                  </p>
                  <Link
                    to="/submit-business"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="add-business-for-locations"
                  >
                    <Plus className="w-4 h-4" />
                    Add Your Business
                  </Link>
                </div>
              )}
            </div>
          ) : activeTab === 'history' ? (
            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Recently Viewed
              </h2>

              {recentlyViewed.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {recentlyViewed.map((business, index) => (
                    <motion.div
                      key={business.business_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      data-testid={`history-business-${business.business_id}`}
                    >
                      <Link
                        to={`/businesses/${business.business_id}`}
                        className="glass-card rounded-xl overflow-hidden hover:border-zinc-700 transition-all group block"
                      >
                        {/* Image */}
                        <div className="relative h-40 bg-zinc-900">
                          {business.image_url ? (
                            <OptimizedImage
                              src={business.image_url}
                              alt={business.name}
                              preset="card"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Building2 className="w-12 h-12 text-zinc-700" />
                            </div>
                          )}
                          
                          {/* Viewed timestamp */}
                          {business.viewed_at && (
                            <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-black/70 backdrop-blur-sm text-xs text-zinc-300 flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {new Date(business.viewed_at).toLocaleDateString()}
                            </div>
                          )}
                          
                          {/* Verification badge */}
                          {business.verification_status === 'verified' && (
                            <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-green-500/20 backdrop-blur-sm text-green-400 text-xs font-medium flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Verified
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-4">
                          <h3 className="font-semibold text-white mb-1 group-hover:text-purple-400 transition-colors line-clamp-1">
                            {business.name}
                          </h3>
                          <p className="text-zinc-500 text-sm mb-2">{business.category}</p>
                          
                          <div className="flex items-center gap-3 text-sm">
                            {business.average_rating > 0 && (
                              <div className="flex items-center gap-1 text-yellow-400">
                                <Star className="w-4 h-4 fill-current" />
                                <span>{business.average_rating.toFixed(1)}</span>
                              </div>
                            )}
                            
                            {business.is_online_only ? (
                              <div className="flex items-center gap-1 text-blue-400">
                                <Globe className="w-4 h-4" />
                                <span>Online</span>
                              </div>
                            ) : business.city && (
                              <div className="flex items-center gap-1 text-zinc-500">
                                <MapPin className="w-4 h-4" />
                                <span>{business.city}, {business.state}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="glass-card rounded-xl p-12 text-center">
                  <History className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Viewing History</h3>
                  <p className="text-zinc-500 mb-6">
                    Businesses you view will appear here for easy access.
                  </p>
                  <Link
                    to="/businesses"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="explore-businesses-history"
                  >
                    <Building2 className="w-4 h-4" />
                    Explore Businesses
                  </Link>
                </div>
              )}
            </div>
          ) : activeTab === 'products' ? (
            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Manage Products & Services
              </h2>

              {businesses.filter(b => b.is_claimed && b.verification_status === 'verified' && b.marketplace_enabled !== false).length > 0 ? (
                <div className="space-y-6">
                  {/* Business Selector */}
                  <div className="flex flex-wrap gap-2">
                    {businesses
                      .filter(b => b.is_claimed && b.verification_status === 'verified' && b.marketplace_enabled !== false)
                      .map((biz) => (
                        <button
                          key={biz.business_id}
                          onClick={() => setSelectedProductsBusiness(biz)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            selectedProductsBusiness?.business_id === biz.business_id
                              ? 'bg-white text-black'
                              : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white'
                          }`}
                          data-testid={`products-select-${biz.business_id}`}
                        >
                          {biz.name}
                        </button>
                      ))}
                  </div>

                  {/* Products Manager */}
                  {selectedProductsBusiness ? (
                    <BusinessProducts
                      businessId={selectedProductsBusiness.business_id}
                      isOwner={true}
                    />
                  ) : (
                    <div className="glass-card rounded-xl p-12 text-center">
                      <ShoppingBag className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-white mb-2">Select a Business</h3>
                      <p className="text-zinc-500">
                        Choose one of your businesses above to manage its products and services.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="glass-card rounded-xl p-12 text-center">
                  <ShoppingBag className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Eligible Businesses</h3>
                  <p className="text-zinc-500 mb-6">
                    You need a verified and claimed business with marketplace enabled to sell products.
                  </p>
                  <Link
                    to="/submit-business"
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="add-business-for-products"
                  >
                    <Plus className="w-4 h-4" />
                    Add Your Business
                  </Link>
                </div>
              )}
            </div>
          ) : activeTab === 'delivery' ? (
            <div>
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
                Delivery Driver
              </h2>
              <DeliveryDriverDashboard />
            </div>
          ) : null}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Business</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete this business? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Boost & Badge Dialog */}
      <BoostBadgeDialog
        open={boostDialogOpen}
        onOpenChange={setBoostDialogOpen}
        business={businessToBoost}
        walletBalance={walletBalance}
        onPurchaseComplete={refreshWalletBalance}
      />

      {/* Create Online Version Dialog */}
      <AlertDialog open={createOnlineDialogOpen} onOpenChange={setCreateOnlineDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-purple-400" />
              Create Online Version
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Create an online listing for &quot;{businessForOnline?.name}&quot; to reach customers beyond your local area.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <p className="text-purple-400 text-sm">
                This will create a separate online listing linked to your physical location(s). 
                Customers searching online will find your online listing, while local customers will find your physical location.
              </p>
            </div>
            
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Service Area</label>
              <select
                value={onlineFormData.service_area}
                onChange={(e) => setOnlineFormData({ ...onlineFormData, service_area: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-white"
              >
                <option value="Nationwide">Nationwide (USA)</option>
                <option value="Global">Global (Worldwide)</option>
                <option value="Regional">Regional</option>
              </select>
            </div>
            
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Description for Online Listing</label>
              <Textarea
                value={onlineFormData.description}
                onChange={(e) => setOnlineFormData({ ...onlineFormData, description: e.target.value })}
                placeholder="Describe your online services..."
                className="bg-zinc-900 border-zinc-800 text-white"
                rows={3}
              />
              <p className="text-zinc-500 text-xs mt-1">You can customize this later</p>
            </div>

            <div className="flex items-start gap-2 text-xs text-zinc-500">
              <CheckCircle className="w-4 h-4 shrink-0 mt-0.5 text-green-400" />
              <p>The online listing will link to all your physical locations, so customers can visit in person too.</p>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCreateOnlineVersion}
              disabled={creatingOnline}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500"
              data-testid="confirm-create-online-button"
            >
              {creatingOnline ? 'Creating...' : 'Create Online Listing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <AlertDialog open={passwordChangeOpen} onOpenChange={setPasswordChangeOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-purple-400" />
              Change Password
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Enter your current password and choose a new password.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Current Password</label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="bg-zinc-900 border-zinc-800 text-white pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">New Password</label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="bg-zinc-900 border-zinc-800 text-white pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
              {newPassword && newPassword.length < 8 && (
                <p className="text-red-400 text-xs mt-1">Password must be at least 8 characters</p>
              )}
            </div>
            
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Confirm New Password</label>
              <Input
                type={showNewPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="bg-zinc-900 border-zinc-800 text-white"
                data-testid="confirm-password-input"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
              )}
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800"
              onClick={() => {
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleChangePassword}
              disabled={changingPassword || !currentPassword || newPassword.length < 8 || newPassword !== confirmPassword}
              className="bg-purple-600 text-white hover:bg-purple-700"
              data-testid="confirm-change-password-button"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
